import React from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ArrowRight, CaretDown, WarningCircle, X } from "@phosphor-icons/react";
import { ANDatePicker } from "@answersnow/pkg-theme";
import { format } from "date-fns";
import AdditionalInformationSection, {
  AdditionalAttachment,
} from "./AdditionalInformationSection";
import OverrideChip from "./OverrideChip";

export interface DischargeReportFormState {
  dateOfDischarge: Date | null;
  reasonForDischarge: string;
  externalProviderKey: string;
  reasonsToResumeServices: string;
  recommendedNextSteps: string;
}

export const INITIAL_DISCHARGE_REPORT_FORM: DischargeReportFormState = {
  dateOfDischarge: null,
  reasonForDischarge: "",
  externalProviderKey: "",
  reasonsToResumeServices: "",
  recommendedNextSteps: "",
};

export const isDischargeReportFormValid = (form: DischargeReportFormState): boolean =>
  form.reasonForDischarge.trim() !== "" &&
  form.reasonsToResumeServices.trim() !== "" &&
  form.recommendedNextSteps.trim() !== "";

export interface ExternalProviderOption {
  key: string;
  physicianId: number | null;
  clinicId: number | null;
  name: string;
  physicianType: string;
  frequencyCareCoordination: string | null;
  releaseSignedDate: string | null;
  releaseExpiresDate: string | null;
  hasCollaborationNotes: boolean;
}

export interface DischargeReportPayload {
  dateOfDischarge: string | null;
  reasonForDischarge: string;
  reasonsToResumeServices: string;
  recommendedNextSteps: string;
}

export const buildDischargeReportPayload = (
  form: DischargeReportFormState
): DischargeReportPayload => ({
  dateOfDischarge: form.dateOfDischarge ? format(form.dateOfDischarge, "PP") : null,
  reasonForDischarge: form.reasonForDischarge,
  reasonsToResumeServices: form.reasonsToResumeServices,
  recommendedNextSteps: form.recommendedNextSteps,
});

interface DischargeReportStepProps {
  /** Whether an active override waives this document's readiness checks */
  isOverridden?: boolean;
  form: DischargeReportFormState;
  onFieldChange: <K extends keyof DischargeReportFormState>(
    field: K,
    value: DischargeReportFormState[K]
  ) => void;
  providerOptions: ExternalProviderOption[];
  onClose: () => void;
  onContinue: () => void;
  additionalInformation: string;
  onAdditionalInformationChange: (value: string) => void;
  additionalFiles: AdditionalAttachment[];
  onAdditionalFilesChange: (files: AdditionalAttachment[]) => void;
}

const DischargeReportStep: React.FC<DischargeReportStepProps> = ({
  form,
  onFieldChange,
  providerOptions,
  onClose,
  onContinue,
  additionalInformation,
  onAdditionalInformationChange,
  additionalFiles,
  onAdditionalFilesChange,
  isOverridden = false,
}) => {
  const selectedProvider =
    form.externalProviderKey === ""
      ? null
      : providerOptions.find((p) => p.key === form.externalProviderKey) ?? null;

  const missingProviderFields = selectedProvider
    ? ([
        !selectedProvider.name.trim() && "Name",
        !selectedProvider.physicianType.trim() && "Type",
        !selectedProvider.releaseSignedDate && "PHI signed date",
        !selectedProvider.releaseExpiresDate && "PHI expiration date",
        !selectedProvider.hasCollaborationNotes && "Collaboration Notes",
      ].filter(Boolean) as string[])
    : [];

  const hasIncompleteProvider = missingProviderFields.length > 0;

  return (
    <>
      <DialogTitle>
        <Box>
          <Typography variant="h4" component="h2">
            Discharge Report
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Document reasons and recommended next steps upon discharge from services.
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          aria-label="Close dialog"
          size="small"
          sx={{ color: "text.secondary" }}
        >
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ "& .MuiFormLabel-asterisk": { color: "error.main" } }}>
        <Stack sx={{ mt: 7, gap: 5 }}>
          {isOverridden && <OverrideChip />}
          <ANDatePicker
            label="Date of discharge"
            value={form.dateOfDischarge}
            onChange={(date: Date | null) => onFieldChange("dateOfDischarge", date)}
            placeholder="Pick a date"
            format="PP"
            slots={{ textField: TextField }}
            slotProps={{
              field: { fullWidth: true },
              popper: { disablePortal: false, style: { zIndex: 4000 } },
            }}
          />

          <TextField
            label="Reason for discharge"
            required
            fullWidth
            multiline
            minRows={4}
            value={form.reasonForDischarge}
            onChange={(e) => onFieldChange("reasonForDischarge", e.target.value)}
            helperText="Please provide a description of why the client is transitioning out of AnswersNow services."
          />

          <TextField
            label="External provider"
            select
            fullWidth
            value={form.externalProviderKey}
            onChange={(e) => onFieldChange("externalProviderKey", e.target.value)}
            helperText="If the client is transitioning to services with another provider, please specify here."
            SelectProps={{
              IconComponent: CaretDown,
              displayEmpty: true,
              renderValue: (selected) => {
                const name = providerOptions.find((opt) => opt.key === selected)?.name;
                return name ? (
                  name
                ) : (
                  <Box component="em" sx={{ color: "text.disabled" }}>
                    Select external provider
                  </Box>
                );
              },
              sx: { "& .MuiSelect-icon": { color: "action.active" } },
            }}
          >
            <MenuItem value="">None</MenuItem>
            {providerOptions.map((opt) => (
              <MenuItem key={opt.key} value={opt.key}>
                {opt.name}
              </MenuItem>
            ))}
          </TextField>

          {hasIncompleteProvider && (
            <Alert severity="error" icon={<WarningCircle size={22} />}>
              <AlertTitle>Complete this provider’s record to continue</AlertTitle>
              {selectedProvider?.name || "This provider"} can’t be used for the report until these
              details are addressed:
              <Box component="ul" sx={{ mt: 1, mb: 0, pl: 5 }}>
                {missingProviderFields.map((field) => (
                  <li key={field}>{field}</li>
                ))}
              </Box>
            </Alert>
          )}

          <TextField
            label="Reasons to resume services with AnswersNow"
            required
            fullWidth
            multiline
            minRows={4}
            placeholder="(e.g., regression, resurgence of previously-targeted reduction behavior, life changes that would result in increased ability to participate in services)."
            value={form.reasonsToResumeServices}
            onChange={(e) => onFieldChange("reasonsToResumeServices", e.target.value)}
            helperText="Provide a description of the client and family’s specific circumstances that would warrant a return to AnswersNow services."
          />

          <TextField
            label="Recommended next steps"
            required
            fullWidth
            multiline
            minRows={4}
            value={form.recommendedNextSteps}
            onChange={(e) => onFieldChange("recommendedNextSteps", e.target.value)}
            helperText="Provide information to the client/caregiver around recommended followup, including referrals or recommendations for other services."
          />

          <AdditionalInformationSection
            additionalInformation={additionalInformation}
            onAdditionalInformationChange={onAdditionalInformationChange}
            files={additionalFiles}
            onFilesChange={onAdditionalFilesChange}
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button variant="contained" size="medium" color="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          endIcon={<ArrowRight size={16} />}
          disabled={(!isDischargeReportFormValid(form) || hasIncompleteProvider) && !isOverridden}
          onClick={onContinue}
        >
          Continue To Confirm
        </Button>
      </DialogActions>
    </>
  );
};

export default DischargeReportStep;
